import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoPipe } from '@jsverse/transloco';
import { AppMaterialModule } from '../../../../../app-material.module';
import { LanguageToggleComponent } from '../../../../../shared/components/language-toggle/language-toggle.component';
import { ThemeSwitcherComponent } from '../../../../../shared/components/theme-switcher/theme-switcher.component';
import { HeaderNotificationsBellComponent } from '../../../../../shared/components/header-notifications-bell/header-notifications-bell.component';
import { ImpersonationChipComponent } from '../../../../../shared/components/impersonation-chip/impersonation-chip.component';
import { AuthService } from '../../../../../shared/services/auth.service';
import { NavigationService } from '../../../../../shared/services/navigation.service';
import { TermosDeUsoDialogComponent } from '../../../../../shared/components/termos-de-uso-dialog/termos-de-uso-dialog.component';
import { TelegramInfoDialogComponent } from '../../../../../shared/components/dialogs/telegram-info-dialog/telegram-info-dialog.component';
import { buildHubChangePasswordUrl, buildHubPreferencesUrl } from '../../../../../shared/utils/hub-redirect';
import { V2ShellLayoutStore } from '../../services/v2-shell-layout.store';

@Component({
    selector: 'app-v2-shell-header',
    standalone: true,
    imports: [
        CommonModule,
        AppMaterialModule,
        TranslocoPipe,
        LanguageToggleComponent,
        ThemeSwitcherComponent,
        HeaderNotificationsBellComponent,
        ImpersonationChipComponent,
    ],
    templateUrl: './v2-shell-header.component.html',
    styleUrl: './v2-shell-header.component.scss',
    // mat-menu overlay is attached to document.body; styles live in this file under .v2-shell-header-menu
    encapsulation: ViewEncapsulation.None,
})
export class V2ShellHeaderComponent {
    @Input() nomeCliente = '';
    @Input() isAdm = false;
    @Input() ratoeiraHubUrl = '';
    @Input() showMenuToggle = true;

    @Output() menuToggle = new EventEmitter<void>();
    @Output() toggleAi = new EventEmitter<void>();

    public readonly accountMenuItems: { icon: string; labelKey: string; action: () => void }[] = [
        { icon: 'menu_book', labelKey: 'menu.userMenu.accessEbook', action: () => this.acessarEbook() },
        {
            icon: 'lock_reset',
            labelKey: 'menu.userMenu.changePassword',
            action: () => this.abrirHubEmNovaAba(buildHubChangePasswordUrl()),
        },
        {
            icon: 'schedule',
            labelKey: 'menu.userMenu.changeTimezone',
            action: () => this.abrirHubEmNovaAba(buildHubPreferencesUrl()),
        },
        {
            icon: 'workspace_premium',
            labelKey: 'menu.userMenu.subscriptionData',
            action: () => this.navigationService.goTo('/v2/dados-assinatura'),
        },
        { icon: 'send', labelKey: 'menu.userMenu.configTelegram', action: () => this.abrirModalTelegram() },
        { icon: 'description', labelKey: 'menu.userMenu.termsOfUse', action: () => this.termosUso() },
        { icon: 'policy', labelKey: 'menu.userMenu.policies', action: () => this.acessarPoliticas() },
    ];

    public isStaffSession: boolean = this.authService.getImpersonationInfo().isImpersonating;

    constructor(
        private layoutStore: V2ShellLayoutStore,
        private authService: AuthService,
        private navigationService: NavigationService,
        private router: Router,
        private dialog: MatDialog,
    ) {}

    public openAdmin(): void {
        void this.router.navigate(['/admin/clientes']);
    }

    public get isMobile(): boolean {
        return this.layoutStore.isMobile;
    }

    get isDesktop(): boolean {
        return this.layoutStore.isDesktop;
    }

    get isCollapsed(): boolean {
        return this.layoutStore.isCollapsed;
    }

    onMenuToggle(): void {
        this.menuToggle.emit();
    }

    goToRatoeiraHub(): void {
        if (!this.ratoeiraHubUrl) {
            return;
        }

        window.open(this.ratoeiraHubUrl, '_blank', 'noopener,noreferrer');
    }

    goTo(route: string): void {
        if (route === '/admin') {
            this.router.navigate([route]);
            return;
        }

        const isProjetoCentral = /^\/v2\/projetos\/[0-9]+\//.test(route);
        if (isProjetoCentral && this.layoutStore.isDesktop && !this.layoutStore.isCollapsed) {
            this.layoutStore.isCollapsed = true;
        }

        this.navigationService.goToContextual(route);
    }

    sair(): void {
        this.authService.logout();
    }

    termosUso(): void {
        this.dialog.open(TermosDeUsoDialogComponent, {
            data: {},
            minWidth: '95%',
            minHeight: '95%',
            width: '95%',
            height: '95%',
            maxWidth: '100vw',
            maxHeight: '100vh',
        });
    }

    acessarPoliticas(): void {
        window.open('https://ratoeiraads.com.br/politica-de-privacidade/', '_blank');
    }

    abrirHubEmNovaAba(url: string): void {
        if (!url) {
            return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }

    abrirModalTelegram(): void {
        const cliente = this.authService.cliente;
        this.dialog.open(TelegramInfoDialogComponent, {
            data: {
                telegram_bot_token: cliente?.telegram_bot_token || '',
                telegram_chat_id: cliente?.telegram_chat_id || '',
            },
            minWidth: '320px',
            maxWidth: '800px',
        });
    }

    acessarEbook(): void {
        window.open('https://drive.google.com/drive/folders/1NJubG51YngF2VnwML_zmgxINd6csoeKg?usp=sharing', '_blank');
    }

    onOpenTutorial(): void {
        const url = this.authService.getHelpCenterUrl();
        this.navigationService.openExternalUrl(url);
    }

    onToggleAi(): void {
        this.toggleAi.emit();
    }
}
